import React, { useState, useEffect } from 'react';
import { 
  Form, 
  FormGroup, 
  Label, 
  Input, 
  Button, 
  Spinner,
  Row,
  Col,
  Badge
} from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import { categoryService } from '../services';
import { Category } from '../types';
import { CATEGORY_HOME_SECTION_KEY_OPTIONS } from '../constants/homeSections';
import Toast from './Toast';

interface CategoryFormProps {
  category?: Category | null;
  onSave: () => void;
  onCancel: () => void;
}

const CategoryForm: React.FC<CategoryFormProps> = ({ category, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    shortDesc: '',
    description: '',
    parentId: '',
    homeSectionKey: '',
    isActive: true,
  });
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [parentCategories, setParentCategories] = useState<Category[]>([]);

  const getParentIdFromCategory = (cat: any): string => {
    if (!cat?.parentId) return '';
    if (typeof cat.parentId === 'string') return cat.parentId;
    if (typeof cat.parentId === 'object' && cat.parentId !== null && (cat.parentId as any)._id) {
      return (cat.parentId as any)._id;
    }
    return '';
  };

  useEffect(() => {
    const initForm = async () => {
      let categoryToUse = category;
      
      // When editing, fetch full category from API to ensure we have parentId
      if (category?._id) {
        try {
          const fullCategory = await categoryService.getOne(category._id);
          categoryToUse = fullCategory;
        } catch (err) {
          console.error('Failed to fetch category for edit', err);
        }
      }

      const categories = await categoryService.getAll();
      const allCats = Array.isArray(categories) ? categories : categories.data || [];
      const parents = allCats.filter((c: any) => !c.parentId && c._id !== categoryToUse?._id);
      setParentCategories(parents);

      if (categoryToUse) {
        const parentIdValue = getParentIdFromCategory(categoryToUse);

        setFormData({
          name: categoryToUse.name || '',
          shortDesc: categoryToUse.shortDesc || '',
          description: categoryToUse.description || '',
          parentId: parentIdValue,
          homeSectionKey: categoryToUse.homeSectionKey || '',
          isActive: categoryToUse.isActive !== undefined ? categoryToUse.isActive : true,
        });
        if (categoryToUse.image) {
          setImagePreview(categoryToUse.image);
        }
        if ((categoryToUse as any).icon) {
          setIconPreview((categoryToUse as any).icon);
        }
      }
    };
    initForm();
  }, [category]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setImage(file);
        setImagePreview(URL.createObjectURL(file));
      }
    },
  });

  const { getRootProps: getIconRootProps, getInputProps: getIconInputProps } = useDropzone({
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.svg'] },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setIconFile(file);
        setIconPreview(URL.createObjectURL(file));
      }
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = new FormData();
      data.append('name', formData.name);
      if (formData.shortDesc) data.append('shortDesc', formData.shortDesc);
      if (formData.description) data.append('description', formData.description);
      if (formData.parentId) data.append('parentId', formData.parentId);
      if (!formData.parentId) data.append('homeSectionKey', formData.homeSectionKey);
      data.append('isActive', formData.isActive.toString());
      if (image) data.append('image', image);
      if (iconFile) data.append('icon', iconFile);

      if (category?._id) {
        await categoryService.update(category._id, data);
      } else {
        await categoryService.create(data);
      }

      onSave();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to save category';
      setError(errorMessage);
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toast
        message={error}
        type="danger"
        isOpen={showToast}
        onClose={() => {
          setShowToast(false);
          setError('');
        }}
        duration={6000}
      />
      
      <Form onSubmit={handleSubmit}>
        <Row>
        <Col md={12}>
          <FormGroup>
            <Label htmlFor="name" className="form-label fw-semibold">
              Category Name <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              id="name"
              name="name"
              placeholder="Enter category name"
              value={formData.name}
              onChange={handleChange}
              required
              className="form-control"
            />
          </FormGroup>
        </Col>

        <Col md={12}>
          <FormGroup>
            <Label htmlFor="shortDesc" className="form-label fw-semibold">
              Short Description
            </Label>
            <Input
              type="textarea"
              id="shortDesc"
              name="shortDesc"
              placeholder="Brief description of category"
              value={formData.shortDesc}
              onChange={handleChange}
              rows={2}
              className="form-control"
            />
          </FormGroup>
        </Col>

        <Col md={12}>
          <FormGroup>
            <Label htmlFor="description" className="form-label fw-semibold">
              Full Description
            </Label>
            <Input
              type="textarea"
              id="description"
              name="description"
              placeholder="Detailed description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              className="form-control"
            />
          </FormGroup>
        </Col>

        {/* Show parent dropdown when we have parent categories - for add (parent or sub) or edit */}
        {parentCategories.length > 0 && (
          <Col md={12}>
            <FormGroup>
              <Label htmlFor="parentId" className="form-label fw-semibold">
                Parent Category
              </Label>
              <Input
                type="select"
                id="parentId"
                name="parentId"
                value={formData.parentId}
                onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                className="form-control"
              >
                <option value="">None (Top Level)</option>
                {parentCategories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        )}

        {!formData.parentId && (
          <Col md={12}>
            <FormGroup>
              <Label htmlFor="homeSectionKey" className="form-label fw-semibold">
                Home section key
              </Label>
              <Input
                type="select"
                id="homeSectionKey"
                name="homeSectionKey"
                value={formData.homeSectionKey}
                onChange={(e) => setFormData({ ...formData, homeSectionKey: e.target.value })}
                className="form-control"
              >
                {CATEGORY_HOME_SECTION_KEY_OPTIONS.map((opt) => (
                  <option key={opt.value || 'none'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Input>
              <small className="text-muted">
                Links this parent category to a fixed home section key. The category name is shown in the app
                (e.g. Webinars with key dailyReset).
              </small>
            </FormGroup>
          </Col>
        )}

        <Col md={12}>
          <FormGroup>
            <Label className="form-label fw-semibold">
              Icon image <span className="text-muted fw-normal">(optional)</span>
            </Label>
            <div
              {...getIconRootProps()}
              className="border border-2 border-dashed rounded-3 p-4 text-center"
              style={{
                cursor: 'pointer',
                borderColor: '#ced4da',
                backgroundColor: '#f8f9fa',
                transition: 'all 0.3s ease',
              }}
            >
              <input {...getIconInputProps()} />
              {iconPreview ? (
                <div>
                  <img
                    src={iconPreview}
                    alt="Icon preview"
                    className="mb-2 rounded"
                    style={{ maxWidth: '80px', maxHeight: '80px', objectFit: 'contain' }}
                  />
                  <p className="text-muted mb-0 small">
                    <i className="ri-image-edit-line me-1"></i>
                    Drop a new icon or click to replace
                  </p>
                </div>
              ) : (
                <div>
                  <i className="ri-image-add-line fs-2 text-muted mb-2"></i>
                  <p className="text-muted mb-0 small">
                    Drop icon image here or click to browse
                  </p>
                  <small className="text-muted">PNG, JPG, WEBP, SVG. Small icon for sidebar.</small>
                </div>
              )}
            </div>
          </FormGroup>
        </Col>

        {/* Show parent category name when creating subcategory (has parentId but no _id) - read-only */}
        {category?.parentId && !category._id && parentCategories.length === 0 && (
          <Col md={12}>
            <FormGroup>
              <Label className="form-label fw-semibold">
                Parent Category
              </Label>
              <div className="p-2 bg-light rounded">
                <i className="ri-folder-line me-2 text-primary"></i>
                <span className="fw-medium">
                  {parentCategories.find(cat => cat._id === formData.parentId)?.name || 'Selected Parent'}
                </span>
                <Badge color="info" className="ms-2">Auto-selected</Badge>
              </div>
            </FormGroup>
          </Col>
        )}

        <Col md={12}>
          <FormGroup>
            <Label className="form-label fw-semibold">Category Image</Label>
            <div
              {...getRootProps()}
              className="border border-2 border-dashed rounded-3 p-4 text-center"
              style={{
                cursor: 'pointer',
                borderColor: '#ced4da',
                backgroundColor: '#f8f9fa',
                transition: 'all 0.3s ease'
              }}
            >
              <input {...getInputProps()} />
              {imagePreview ? (
                <div>
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="mb-3 rounded" 
                    style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'cover' }} 
                  />
                  <p className="text-muted mb-0">
                    <i className="ri-image-edit-line me-2"></i>
                    Drop a new image or click to replace
                  </p>
                </div>
              ) : (
                <div>
                  <i className="ri-upload-cloud-2-line display-4 text-muted mb-2"></i>
                  <p className="text-muted mb-0">
                    Drop category image here or click to browse
                  </p>
                  <small className="text-muted">Supports: PNG, JPG, JPEG, WEBP</small>
                </div>
              )}
            </div>
          </FormGroup>
        </Col>
      </Row>

      <div className="d-flex gap-2 justify-content-end mt-4">
        <Button type="button" color="light" onClick={onCancel}>
          <i className="ri-close-line me-1"></i>
          Cancel
        </Button>
        <Button type="submit" color="primary" disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            <>
              <i className="ri-save-line me-1"></i>
              {category?._id ? 'Update' : 'Create'} Category
            </>
          )}
        </Button>
      </div>
      </Form>
    </>
  );
};

export default CategoryForm;
